import { Peak } from './types';
import { Point } from '../../../../context/context';

export function toMinutes(t: string) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

export function generateTimeLabels(start: string, end: string, interval: number = 15): string[] {
  const toMinutesLocal = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };
  const toTime = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  let cur = toMinutesLocal(start), endM = toMinutesLocal(end);
  const times: string[] = [];
  while (cur <= endM) {
    times.push(toTime(cur));
    cur += interval;
  }
  return times;
}

export function snapTimeToNearestLabel(time: string, labels: string[]): string {
  if (labels.length === 0) {
    return time;
  }

  return labels.reduce((closest, candidate) => (
    Math.abs(toMinutes(closest) - toMinutes(time)) < Math.abs(toMinutes(candidate) - toMinutes(time))
      ? closest
      : candidate
  ));
}

export function generateTimes(start: string, end: string, interval: number): string[] {
  let cur = toMinutes(start), endM = toMinutes(end);
  const times: string[] = [];
  while (cur <= endM) {
    times.push(`${String(Math.floor(cur / 60)).padStart(2, '0')}:${String(cur % 60).padStart(2, '0')}`);
    cur += interval;
  }
  return times;
}

export function createTimeline(times: string[], peaks: Peak[]): number[] {
  const p = Array(times.length).fill(1);
  if (times.length === 0 || peaks.length === 0) return p;

  // Infer interval from time labels (assumes HH:mm and monotonically increasing within same day)
  const intervalMinutes = (() => {
    if (times.length < 2) return 15;
    const delta = toMinutes(times[1]) - toMinutes(times[0]);
    return delta > 0 ? delta : 15;
  })();
  const slotsPerHour = 60 / intervalMinutes;

  peaks.forEach(peak => {
    const idx = times.findIndex(t => t === peak.peak_time);
    if (idx === -1) return;
    const peakDiff = peak.effect - 1;

    // Widths are configured in hours; convert to number of timeline slots.
    const widthLeftSlots = Math.max(0, Math.round(peak.width_left * slotsPerHour));
    const widthRightSlots = Math.max(0, Math.round(peak.width_right * slotsPerHour));

    for (let offset = -widthLeftSlots; offset <= widthRightSlots; offset++) {
      const i = idx + offset;
      if (i < 0 || i >= p.length) continue;

      const sideSlots = offset <= 0 ? widthLeftSlots : widthRightSlots;
      const offsetAbs = Math.abs(offset);

      // When width is 0, the peak affects only the center slot.
      const additional = sideSlots === 0 ? peakDiff : peakDiff * (1 - offsetAbs / sideSlots);
      p[i] = Math.max(1, p[i] + additional);
    }
  });
  return p;
}

export function randomPointInPolygon(polygon: [number, number][]): [number, number] {
  // Simple bounding box rejection sampling
  const lats = polygon.map(([lat]) => lat);
  const lngs = polygon.map(([, lng]) => lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  let pt: [number, number];
  let tries = 0;
  do {
    pt = [minLat + Math.random() * (maxLat - minLat), minLng + Math.random() * (maxLng - minLng)];
    tries++;
    if (tries > 1000) break;
  } while (!pointInPolygon(pt, polygon));
  return pt;
}

export function pointInPolygon(point: [number, number], polygon: [number, number][]): boolean {
  // Ray-casting algorithm
  let x = point[0], y = point[1];
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    let xi = polygon[i][0], yi = polygon[i][1];
    let xj = polygon[j][0], yj = polygon[j][1];
    let intersect = ((yi > y) !== (yj > y)) &&
      (x < (xj - xi) * (y - yi) / (yj - yi + 0.0000001) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

export function weightedRandom<T>(items: T[], weights: number[]): T {
  const total = weights.reduce((a, b) => a + b, 0);
  let r = Math.random() * total;
  for (let i = 0; i < items.length; i++) {
    if (r < weights[i]) return items[i];
    r -= weights[i];
  }
  return items[items.length - 1];
}

export function weightedRandomTime(start: Date, end: Date, peaks: Peak[]): Date {
  // Build a time distribution array (e.g. 15-min intervals)
  const interval = 15; // minutes
  const times: Date[] = [];
  const labels: string[] = [];
  const startM = start.getTime();
  const endM = end.getTime();
  const n = Math.floor((endM - startM) / (interval * 60 * 1000));
  for (let i = 0; i <= n; i++) {
    const t = new Date(startM + i * interval * 60 * 1000);
    times.push(t);
    labels.push(`${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`);
  }
  const weights = peaks.length > 0 ? createTimeline(labels, peaks) : Array(labels.length).fill(1);
  return weightedRandom(times, weights);
}

export function makePoint(coords: [number, number], name: string, color: string): Point {
  return {
    id: '',
    type: 'point',
    name,
    description: '',
    colour: color,
    icon: 'location',
    size: 24,
    isLocked: false,
    coordinates: coords,
  };
} 
